import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Dayjs } from "dayjs";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HistoryIcon from "@mui/icons-material/History";
import BlockIcon from "@mui/icons-material/Block";
import PrintIcon from "@mui/icons-material/Print";
import { getListReprintAuth } from "../../services";
import {
  ReprintAuthRow,
  ReprintStatusFilter,
} from "../../types/reprintAuth.type";
import { formatDate } from "../../utils/date";
import ReprintAuthPagination from "./Pagination";
import ReprintAuthSkeleton from "./Skeleton";
import PrintHistoryModal from "./PrintHistoryModal";
import GrantDetailModal from "./GrantDetailModal";
import RevokeDialog from "./RevokeDialog";
import {
  statusChipOf,
  TRANSACTION_TYPE_LABEL,
  RiskView,
  isRisky,
  isNearExpiry,
  isOverPrint,
  riskRowBg,
  getRemainingText,
  roleLabel,
} from "./data";

interface ReprintAuthTableProps {
  search: string;
  status: string;
  routeCode: string;
  createdBy: string;
  dateFrom: Dayjs | null;
  dateTo: Dayjs | null;
  riskView: RiskView;
  triggerFetch: number;
  onRefresh: () => void;
  updatedAt: string;
  setUpdatedAt: Dispatch<SetStateAction<string>>;
}

const COLUMN_COUNT = 8;
const LIMIT = 10;
const CELL_PADDING = 8;

/**
 * A row's tallest cell holds a `size="small"` IconButton (a 20px icon inside 5px
 * padding). The loading skeleton is held at the same height so the table does not
 * resize when the rows arrive.
 */
const ROW_HEIGHT = 30 + CELL_PADDING * 2;

export default function ReprintAuthTable({
  search,
  status,
  routeCode,
  createdBy,
  dateFrom,
  dateTo,
  riskView,
  triggerFetch,
  onRefresh,
  updatedAt,
  setUpdatedAt,
}: ReprintAuthTableProps) {
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(LIMIT);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [rows, setRows] = useState<ReprintAuthRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Modal state.
  const [historyTxn, setHistoryTxn] = useState<ReprintAuthRow | null>(null);
  const [detailGrant, setDetailGrant] = useState<ReprintAuthRow | null>(null);
  const [revokeGrant, setRevokeGrant] = useState<ReprintAuthRow | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await getListReprintAuth({
        page,
        limit,
        status: status as ReprintStatusFilter,
        q: search || undefined,
        route_code: routeCode,
        created_by: createdBy,
        date_from: dateFrom ? dateFrom.format("YYYY-MM-DD") : undefined,
        date_to: dateTo ? dateTo.format("YYYY-MM-DD") : undefined,
      });

      setRows((data.rows ?? []) as ReprintAuthRow[]);
      setTotalCount(data.count ?? 0);
      setUpdatedAt(formatDate(new Date().toISOString(), "DD/MM/YYYY HH:mm"));
    } finally {
      setLoading(false);
    }
  }, [
    page,
    limit,
    status,
    search,
    routeCode,
    createdBy,
    dateFrom,
    dateTo,
    setUpdatedAt,
  ]);

  useEffect(() => {
    fetchData();
  }, [triggerFetch, fetchData]);

  // Reset to first page whenever a filter changes.
  useEffect(() => {
    setPage(1);
  }, [search, status, routeCode, createdBy, dateFrom, dateTo]);

  // Client-side risk view (advisory) — filters the current page's rows.
  const visibleRows = rows.filter((row) => {
    if (riskView === "near_expiry") return isNearExpiry(row);
    if (riskView === "over_print") return isOverPrint(row);
    return true;
  });

  const headerCellSx = {
    borderRight: (theme: import("@mui/material/styles").Theme) =>
      `1px solid ${theme.palette.draft.main}`,
  };

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead sx={{ bgcolor: (theme) => theme.palette.background.default }}>
            <TableRow>
              <TableCell sx={headerCellSx}>เลขที่บิล</TableCell>
              <TableCell sx={headerCellSx}>ลูกค้า</TableCell>
              <TableCell sx={headerCellSx}>สายส่ง</TableCell>
              <TableCell sx={headerCellSx}>สร้างโดย</TableCell>
              <TableCell sx={headerCellSx}>หมดอายุ</TableCell>
              <TableCell sx={headerCellSx} align="center">
                พิมพ์ซ้ำ
              </TableCell>
              <TableCell sx={headerCellSx} align="center">
                สถานะ
              </TableCell>
              <TableCell align="center">จัดการ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <ReprintAuthSkeleton
                columns={COLUMN_COUNT}
                rows={limit}
                rowHeight={ROW_HEIGHT}
              />
            )}

            {!loading && visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบข้อมูลตามเงื่อนไข
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              visibleRows.map((row, index) => {
                const chip = statusChipOf(row);
                const risky = isRisky(row);
                return (
                  <TableRow
                    key={row.uuid}
                    onClick={() => setDetailGrant(row)}
                    sx={{
                      cursor: "pointer",
                      bgcolor:
                        riskRowBg(row) ?? (index % 2 === 1 ? "#f6f6f9" : "#fff"),
                      height: ROW_HEIGHT,
                      "& td, & th": { padding: `${CELL_PADDING}px` },
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        {risky && (
                          <Tooltip
                            title={
                              isOverPrint(row)
                                ? "พิมพ์ซ้ำเกินกำหนด"
                                : "ใกล้หมดอายุ"
                            }
                          >
                            <WarningAmberIcon
                              fontSize="small"
                              sx={{
                                color: isOverPrint(row) ? "#fb3b3b" : "#FDA42C",
                              }}
                            />
                          </Tooltip>
                        )}
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {row.transaction_no}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {TRANSACTION_TYPE_LABEL[row.transaction_type]}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.customer_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.customer_code}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.route_code || "-"}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {row.created_by_name || "-"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {roleLabel(row.created_by_role)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {row.valid_until
                          ? formatDate(row.valid_until, "DD/MM/YYYY HH:mm")
                          : "-"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getRemainingText(row)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="ดูประวัติการพิมพ์บิล">
                        <IconButton
                          size="small"
                          onClick={() => setHistoryTxn(row)}
                        >
                          <Badge
                            badgeContent={row.reprint_count}
                            color={row.reprint_count >= 2 ? "error" : "primary"}
                            showZero
                          >
                            <PrintIcon fontSize="small" />
                          </Badge>
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Chip size="small" label={chip.label} color={chip.color} />
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Box display="flex" justifyContent="center" gap={0.5}>
                        <Tooltip title="ประวัติ">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => setHistoryTxn(row)}
                          >
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {row.status === "ACTIVE" && (
                          <Tooltip title="ยกเลิกสิทธิ์">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setRevokeGrant(row)}
                            >
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={2}
        pt={1.5}
        sx={{ flexWrap: "wrap", gap: 1 }}
      >
        <Typography variant="body2" color="text.secondary">
          ทั้งหมด {totalCount} รายการ
        </Typography>
        <Typography variant="caption" color="text.secondary">
          อัปเดตล่าสุด {updatedAt || "-"}
        </Typography>
      </Box>

      <ReprintAuthPagination
        totalCount={totalCount}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
      />

      <PrintHistoryModal
        open={Boolean(historyTxn)}
        transactionUuid={historyTxn?.transaction_uuid ?? null}
        transactionNo={historyTxn?.transaction_no}
        onClose={() => setHistoryTxn(null)}
      />

      <GrantDetailModal
        open={Boolean(detailGrant)}
        grant={detailGrant}
        onClose={() => setDetailGrant(null)}
        onOpenPrintHistory={(grant) => {
          setDetailGrant(null);
          setHistoryTxn(grant);
        }}
        onRevoke={(grant) => {
          setDetailGrant(null);
          setRevokeGrant(grant);
        }}
      />

      <RevokeDialog
        open={Boolean(revokeGrant)}
        grant={revokeGrant}
        onClose={() => setRevokeGrant(null)}
        onRevoked={onRefresh}
      />
    </Paper>
  );
}
